import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
  updatePaymentAuthenticationForError,
} from "@bw/shared/api";
import { paymentQueryKeys } from "@bw/shared/hooks";
import {
  formatMoney,
  reconcileSavedPaymentMethodSelection,
} from "@bw/shared/utils";
import type {
  EnrollmentModeQuoteResponse,
  PromotionClaimRequest,
  ProgramPromotionPaymentReview,
  UnifiedEnrollmentResponse,
} from "@bw/shared/types/generated";
import { useEnrollmentQuote, usePromotionPaymentReview } from "../hooks/useEnrollmentQuote";
import {
  CommercialFeeReview,
  hasReadyCommercialReview,
  useCommercialReviewReady,
} from "./CommercialFeeReview";
import {
  useClassPaymentMethods,
  useClassFrontendConfig,
  useClassSetupIntent,
} from "../hooks/usePayment";
import {
  useClaimPromotion,
  useDeclinePromotion,
} from "../hooks/useMyEnrollments";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
  MaterialPaymentMethodPicker,
} from "../../../common/components/payment";
import { PendingPaymentRecovery } from "./PendingPaymentRecovery";

/**
 * The claim/decline surface for a PROMOTED waitlist enrollment.
 *
 * The existing enrollment quote supplies the actual amount and fee review.
 * Membership coverage remains a server decision, so a no-card claim stays usable:
 *   - FREE track  → the backend claims and the list invalidates (no card UI).
 *   - PAID track  → the backend rejects with "stripe_payment_method_id is
 *     required". We catch that, reveal the SAME facility-scoped Stripe spine
 *     EnrollPage uses (PaymentMethodPicker + AddCardForm), and let the player
 *     explicitly pay WITH the chosen card and both reviewed opaque references.
 * SCA/uncertain reconciliation retains the original submitted body; only a
 * definitive new attempt refreshes the review.
 *
 * The API query is facility-scoped, while the underlying Stripe wallet belongs
 * to the organization. Facilities in one organization may alias the same wallet;
 * the payment spine resolves that alias from the server-returned connected account.
 * The query route uses the enrollment's `facility_id` (server-supplied). The raw
 * backend "card required" string is never the terminal state — the player always
 * gets a usable card affordance instead.
 */

/** True when a claim failure means "a card is required" (a paid promoted spot). */
function isCardRequired(err: unknown): boolean {
  if (!isApiError(err)) return false;
  return /stripe_payment_method_id|payment.?method|card (is )?required|non-?zero fee|paymentrequired/i.test(
    `${err.code ?? ""} ${err.message ?? ""}`,
  );
}

export function ClaimSection({
  enrollment,
  canAct,
}: {
  enrollment: UnifiedEnrollmentResponse;
  canAct: () => boolean;
}) {
  const claim = useClaimPromotion();
  const decline = useDeclinePromotion();
  const promotionQ = usePromotionPaymentReview(enrollment.enrollment_id,
    enrollment.status === "promoted");
  const promotionReview = promotionQ.isSuccess && !promotionQ.isError ? promotionQ.data : undefined;
  const matchingReview = promotionReview?.enrollment_id === enrollment.enrollment_id
    && promotionReview.program_id === enrollment.program_id
    && promotionReview.track_id === enrollment.track_id
    && enrollment.enrollment_mode === "full_course"
    && /^[0-9a-f]{64}$/.test(promotionReview.price_quote_version)
    ? promotionReview : undefined;
  const [reviewNow, setReviewNow] = useState(Date.now);
  useEffect(() => {
    setReviewNow(Date.now());
    const deadline = Date.parse(matchingReview?.promotion_expires_at ?? "");
    if (!Number.isFinite(deadline) || deadline <= Date.now()) return;
    const timer = window.setTimeout(() => setReviewNow(Date.now()),
      Math.min(deadline - Date.now() + 1, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [matchingReview]);
  const deadline = Date.parse(matchingReview?.promotion_expires_at ?? "");
  const currentReview = matchingReview && Number.isFinite(deadline)
    && Math.max(reviewNow, Date.now()) < deadline ? matchingReview : undefined;
  const quoteQ = useEnrollmentQuote(
    enrollment.track_id,
    { enrollment_mode: "full_course" },
    enrollment.status === "promoted" && currentReview?.requirement === "commercial_review_required",
  );
  const reviewReady = useCommercialReviewReady(quoteQ.data);
  const matchingQuote = quoteQ.isSuccess && !quoteQ.isError
    && quoteQ.data?.program_id === enrollment.program_id
    && quoteQ.data.track_id === enrollment.track_id
    && quoteQ.data.enrollment_mode === "full_course" ? quoteQ.data : undefined;
  const paidReviewReady = currentReview?.requirement === "owner_certified_unselected"
    || (currentReview?.requirement === "commercial_review_required" && matchingQuote !== undefined && reviewReady);
  type ClaimBody = PromotionClaimRequest & { enrollmentId: string };
  const [submittedPayment, setSubmittedPayment] = useState<{
    body: ClaimBody;
    quote: EnrollmentModeQuoteResponse | undefined;
    review: ProgramPromotionPaymentReview;
    context: string;
  } | null>(null);

  // Once a naked claim comes back "card required" we stay in the payment state
  // for this enrollment until it succeeds.
  const [needsPayment, setNeedsPayment] = useState(false);
  const facilityId = enrollment.facility_id ?? undefined;
  const paymentSelectionContext = `${facilityId ?? "missing"}:${enrollment.enrollment_id}`;
  const hasCapturedPayment =
    submittedPayment !== null &&
    submittedPayment.context === paymentSelectionContext &&
    submittedPayment.body.enrollmentId === enrollment.enrollment_id &&
    submittedPayment.review.enrollment_id === enrollment.enrollment_id &&
    submittedPayment.review.track_id === enrollment.track_id &&
    submittedPayment.review.program_id === enrollment.program_id;
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(null);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [paymentAuthentication, setPaymentAuthentication] = useState<{
    clientSecret: string;
    body: ClaimBody;
  } | null>(null);
  // The facility route for this program's organization wallet, supplied on the
  // enrollment response. Facilities under that organization can alias the same
  // connected account; the wallet barrier coordinates those aliases. Hooks are enabled on the
  // needs-payment flag (set only after a "card required" rejection).
  const methodsQ = useClassPaymentMethods(facilityId, needsPayment);
  const configQ = useClassFrontendConfig(facilityId, needsPayment);
  const setupIntent = useClassSetupIntent();

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const methods = methodsQ.data ?? [];
  const paymentMethodsSettled =
    needsPayment &&
    methodsQ.data !== undefined &&
    !methodsQ.isFetching &&
    !methodsQ.isError;
  useEffect(() => {
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;
    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [methodsQ.data, paymentMethodsSettled, paymentSelectionContext]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;

  const runClaim = useCallback(
    (stripePaymentMethodId?: string) => {
      if (
        cardSetupBusy ||
        !canAct() ||
        (enrollment.status !== "promoted" &&
          !(enrollment.status === "payment_pending" && hasCapturedPayment)) ||
        (submittedPayment !== null && !hasCapturedPayment)
      )
        return;
      if (
        stripePaymentMethodId &&
        !submittedPayment &&
        (!paidReviewReady || !currentReview
          || Date.now() >= Date.parse(currentReview.promotion_expires_at ?? "")
          || (currentReview.requirement === "commercial_review_required" && !hasReadyCommercialReview(matchingQuote)))
      )
        return;
      setActionError(null);
      const body: ClaimBody = submittedPayment?.body ?? {
        enrollmentId: enrollment.enrollment_id,
        ...(stripePaymentMethodId ? { stripe_payment_method_id: stripePaymentMethodId } : {}),
        ...(currentReview?.requirement === "owner_certified_unselected" && stripePaymentMethodId
          ? { promotion_price_quote_version: currentReview.price_quote_version }
          : currentReview?.requirement === "commercial_review_required" && matchingQuote?.commercial_fee?.readiness === "ready"
            ? { enrollment_quote_id: matchingQuote.quote_id, commercial_fee_quote_id: matchingQuote.commercial_fee.fee_quote_id }
            : {}),
      };
      if (body.stripe_payment_method_id && !submittedPayment && currentReview)
        setSubmittedPayment({
          body,
          quote: currentReview.requirement === "commercial_review_required" ? matchingQuote : undefined,
          review: currentReview,
          context: paymentSelectionContext,
        });
      claim.mutate(body, {
        onSuccess: () => setSubmittedPayment(null),
        onError: (e) => {
          // A paid track with no card → reveal the picker and let the player
          // retry with a card, rather than stranding them on the raw error.
          if (isCardRequired(e) && !body.stripe_payment_method_id) {
            setNeedsPayment(true);
            void promotionQ.refetch();
            return;
          }
          const authentication = getPaymentAuthenticationRequired(e);
          if (authentication && body.stripe_payment_method_id) {
            setPaymentAuthentication({
              clientSecret: authentication.clientSecret,
              body,
            });
            return;
          }
          if (isTerminalPaymentAttemptError(e)) {
            setSubmittedPayment(null);
            void quoteQ.refetch();
            void promotionQ.refetch();
          }
          setActionError(
            isApiError(e) ? e.message : "Couldn't claim. Please try again.",
          );
        },
      });
    },
    [
      cardSetupBusy,
      claim,
      enrollment.enrollment_id,
      enrollment.status,
      canAct,
      quoteQ,
      promotionQ,
      currentReview,
      matchingQuote,
      paidReviewReady,
      submittedPayment,
      hasCapturedPayment,
      paymentSelectionContext,
    ],
  );

  async function finishPaymentAuthentication(
    authenticatedClientSecret: string,
  ) {
    const authentication = paymentAuthentication;
    if (
      !authentication ||
      authentication.clientSecret !== authenticatedClientSecret
    ) {
      return;
    }
    try {
      await claim.mutateAsync(authentication.body);
      setSubmittedPayment(null);
      setPaymentAuthentication((current) =>
        current?.clientSecret === authenticatedClientSecret ? null : current,
      );
    } catch (error) {
      setPaymentAuthentication((current) =>
        updatePaymentAuthenticationForError(
          current,
          authenticatedClientSecret,
          error,
        ),
      );
      if (isTerminalPaymentAttemptError(error)) {
        setSubmittedPayment(null);
        void quoteQ.refetch();
        setActionError(
          isApiError(error)
            ? error.message
            : "Couldn't claim. Please try again.",
        );
      }
      throw error;
    }
  }

  function runDecline() {
    if (enrollment.status !== "promoted" || !canAct()) return;
    setActionError(null);
    decline.mutate(enrollment.enrollment_id, {
      onError: (e) =>
        setActionError(
          isApiError(e) ? e.message : "Couldn't decline. Please try again.",
        ),
    });
  }

  const busy = claim.isPending || decline.isPending;
  const paymentLocked = busy || paymentAuthentication !== null || cardSetupBusy;
  const displayedQuote = submittedPayment ? submittedPayment.quote : matchingQuote;
  const displayedReview = submittedPayment?.review ?? currentReview;
  const displayedPrice = displayedQuote ?? displayedReview;
  const disclosure = (
    <>
      {displayedPrice ? (
        <Typography sx={{ fontWeight: 800 }}>
          {formatMoney(displayedPrice.amount, displayedPrice.currency)}
        </Typography>
      ) : null}
      {displayedReview?.requirement === "commercial_review_required" ? (
        <CommercialFeeReview quote={displayedQuote} required={needsPayment} />
      ) : displayedReview ? (
        <Typography variant="body2" color="text.secondary">
          Current class price. Payment terms are checked again when you submit;
          the spot deadline does not hold this price.
        </Typography>
      ) : needsPayment ? (
        <Alert severity="warning">Payment review unavailable. Refresh before paying.</Alert>
      ) : null}
      {!submittedPayment &&
      needsPayment &&
      (!paidReviewReady || promotionQ.isError || quoteQ.isError) ? (
        <Button
          size="small"
          disabled={paymentLocked || promotionQ.isFetching || quoteQ.isFetching}
          onClick={async () => {
            if (submittedPayment || paymentLocked) return;
            const refreshed = await promotionQ.refetch();
            if (refreshed.isSuccess && refreshed.data.requirement === "commercial_review_required")
              await quoteQ.refetch();
          }}
        >
          Refresh payment review
        </Button>
      ) : null}
    </>
  );

  if (enrollment.status === "payment_pending" && !hasCapturedPayment) {
    return (
      <Stack spacing={1.5}>
        {actionError ? <Alert severity="error">{actionError}</Alert> : null}
        <PendingPaymentRecovery key={enrollment.enrollment_id} enrollment={enrollment} canAct={canAct} />
      </Stack>
    );
  }

  if (
    enrollment.status !== "promoted" &&
    !(enrollment.status === "payment_pending" && hasCapturedPayment)
  ) {
    return actionError ? <Alert severity="error">{actionError}</Alert> : null;
  }

  // ---- payment revealed (paid promoted spot) --------------------------------
  if (needsPayment) {
    return (
      <Stack spacing={1.5} aria-label="Payment" sx={{ width: "100%" }}>
        {disclosure}
        {configQ.isError ? (
          <Alert severity="error">
            This facility can&apos;t take card payments yet. Please try again
            later.
          </Alert>
        ) : methodsQ.isError && paymentAuthentication === null ? (
          <PaymentMethodsLoadError
            retrying={methodsQ.isFetching}
            onRetry={() => void methodsQ.refetch()}
          />
        ) : configQ.isLoading || methodsQ.isLoading ? (
          <Stack
            direction="row"
            spacing={1.5}
            role="status"
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={20} />
            <Typography color="text.secondary">
              Loading payment options…
            </Typography>
          </Stack>
        ) : (
          <>
            <MaterialPaymentMethodPicker
              methods={methods}
              selectedId={selectedPmId}
              onSelect={selectPaymentMethod}
              disabled={paymentLocked || submittedPayment !== null}
              paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                facilityId ?? "",
              )}
            />
            {configQ.data ? (
              <MaterialConnectedElementsProvider config={configQ.data}>
                {paymentAuthentication ? (
                  <MaterialPaymentIntentAuthentication
                    clientSecret={paymentAuthentication.clientSecret}
                    onAuthenticated={finishPaymentAuthentication}
                  />
                ) : (
                  <MaterialAddCardForm
                    publishableKey={configQ.data.publishable_key}
                    createSetupIntent={createSetupIntentSecret}
                    onBusyChange={setCardSetupBusy}
                    onCardAdded={async (pmId) => {
                      const refreshed = await methodsQ.refetch();
                      const savedMethod = refreshed.data?.find(
                        (method) => method.payment_method_id === pmId,
                      );
                      if (
                        refreshed.isError ||
                        savedMethod?.is_usable !== true
                      ) {
                        throw new Error(
                          "The saved card is not available yet. Retry to refresh it.",
                        );
                      }
                      selectPaymentMethod(pmId);
                    }}
                  />
                )}
              </MaterialConnectedElementsProvider>
            ) : null}
          </>
        )}

        {actionError ? <Alert severity="error">{actionError}</Alert> : null}

        {paymentAuthentication ? null : (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => runClaim(currentReview?.requirement === "no_card_required"
                && !submittedPayment ? undefined : chargeReadyPmId ?? undefined)}
              disabled={
                paymentLocked ||
                (!submittedPayment && currentReview?.requirement !== "no_card_required"
                  && (!chargeReadyPmId || !paidReviewReady))
              }
            >
              {claim.isPending
                ? "Claiming…"
                : submittedPayment
                  ? "Finish request"
                  : currentReview?.requirement === "no_card_required" ? "Claim spot" : "Pay & claim"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={runDecline}
              disabled={paymentLocked}
            >
              Decline
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  // ---- default: a single naked Claim + Decline ------------------------------
  return (
    <Stack spacing={1} sx={{ alignItems: { sm: "flex-end" } }}>
      {disclosure}
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          onClick={() => runClaim()}
          disabled={busy}
        >
          Claim spot
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={runDecline}
          disabled={busy}
        >
          Decline
        </Button>
      </Stack>
    </Stack>
  );
}
